import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Card } from '@codes/ui-kit/components/ui/card';
import { Image } from '@codes/ui-kit/components/ui/image';
import { useWorkspaceService } from '@web/app/features/home/hooks';
import { getWorkspaceBadge, useCreateWorkspaceDialog } from '@web/app/shared';
import { PageContainer } from '@web/app/shared/components/PageContainer';
import { Plus, RefreshCcw } from 'lucide-react';

import { WorkspaceAdvImage, WorkspaceBasicImage, WorkspaceImage, WorkspaceProImage } from '@codes/assets';
import { useOwnerStore } from '@codes/owner';
import { Button, SharedSpinner, Skeleton } from '@codes/ui-kit';

export const WorkspaceListPage = () => {
    const { myWorkspacesQueryResult, replaceWorkspace } = useWorkspaceService();
    const { owner } = useOwnerStore();

    const isLoading = myWorkspacesQueryResult.isFetching || myWorkspacesQueryResult.isRefetching;

    const { setWorkspaceDialog } = useCreateWorkspaceDialog({});
    const { t } = useTranslation();

    const navigate = useNavigate();

    if (isLoading) {
        return (
            <PageContainer className="flex flex-col animate-enter">
                <Skeleton className="w-44 h-8 mb-6" />
                <Skeleton className="h-9 w-36 mb-6" />
                <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] max-xs:grid-cols-1 gap-4">
                    <Skeleton className="h-[160px] rounded-lg" />
                    <Skeleton className="h-[160px] rounded-lg" />
                    <Skeleton className="h-[160px] rounded-lg" />
                    <Skeleton className="h-[160px] rounded-lg" />
                </div>
            </PageContainer>
        );
    }

    if (myWorkspacesQueryResult.error) {
        return (
            <PageContainer className="flex flex-col">
                <div className="flex flex-col items-center justify-center gap-4 py-20">
                    <div className="w-14 h-14 rounded-full bg-error/10 flex items-center justify-center">
                        <RefreshCcw className="w-6 h-6 text-error" />
                    </div>
                    <div className="text-error text-sm font-medium">{t('workspace.error.fetchList')}</div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => myWorkspacesQueryResult.refetch()}
                        disabled={myWorkspacesQueryResult.isRefetching}
                    >
                        {t('workspace.error.retry')}
                    </Button>
                </div>
            </PageContainer>
        );
    }

    return (
        <PageContainer className="flex flex-col h-full animate-enter">
            <Image className="max-w-[100px]" src={WorkspaceImage} />
            <div className="flex flex-wrap gap-y-2 items-center justify-between mt-4">
                <h1 className="text-xl font-semibold font-display tracking-tight">{t('workspace.list.myWorkspace')}</h1>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => myWorkspacesQueryResult.refetch()}
                    >
                        {!myWorkspacesQueryResult.isRefetching ? (
                            <>
                                <RefreshCcw size={14} />
                                Refresh
                            </>
                        ) : (
                            <SharedSpinner className="w-4 h-4" />
                        )}
                    </Button>
                    <Button size="sm" className="gap-1.5" onClick={() => setWorkspaceDialog(true)}>
                        <Plus size={14} /> {t('workspace.create.addWorkspace')}
                    </Button>
                </div>
            </div>
            <div className="w-full h-px bg-border mt-6 mb-8" />
            <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] max-xs:grid-cols-1 gap-4 animate-stagger">
                {myWorkspacesQueryResult.data?.data.map(workspace => {
                    return (
                        <Card
                            key={workspace.id}
                            className="cursor-pointer hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200"
                            onClick={() => replaceWorkspace(workspace.id as string, workspace.siteId as string)}
                        >
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <div className="truncate text-base font-medium">
                                    {owner?.defWorkspaceId === workspace.id
                                        ? `${owner?.name} Workspace`
                                        : workspace.name}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">{getWorkspaceBadge(workspace.stereo)}</div>
                            <div className="flex justify-end mt-3">
                                {workspace.stereo === 'basic' && (
                                    <Image className="w-[72px] opacity-60" src={WorkspaceBasicImage} />
                                )}
                                {workspace.stereo === 'pro' && (
                                    <Image className="w-[72px] opacity-60" src={WorkspaceProImage} />
                                )}
                                {workspace.stereo === 'ent' && (
                                    <Image className="w-[72px] opacity-60" src={WorkspaceAdvImage} />
                                )}
                            </div>
                        </Card>
                    );
                })}
            </div>
        </PageContainer>
    );
};
